import { boxH, boxW, cableSegment, colOf, kxy, onSeg, pinsW } from './geometry';
import { parseTerms, type PartLookup, type TermContact } from './catalog';
import { isMainPoles, LIB } from './library';
import type { Placed, Project, ProjectSettings, Pt, PType, Sym } from './types';

/*
 * Connectivity analysis. Two union-find structures are built:
 *  - `uf`: electrical connections (wires, pins, T-junctions, interruption points)
 *  - `pu`: potentials — like `uf`, plus pass-through pin pairs of contacts,
 *          fuses and terminals, so a potential follows the current path.
 */

export class UnionFind {
  private p = new Map<string, string>();
  find(a: string): string {
    const p = this.p;
    if (!p.has(a)) {
      p.set(a, a);
      return a;
    }
    while (p.get(a) !== a) {
      p.set(a, p.get(p.get(a)!)!);
      a = p.get(a)!;
    }
    return a;
  }
  union(a: string, b: string) {
    a = this.find(a);
    b = this.find(b);
    if (a !== b) this.p.set(a, b);
  }
  keys() {
    return this.p.keys();
  }
}

export const pinNode = (s: Sym, i: number) => `p|${s.id}|${i}`;
/** Terminal names of a symbol: its own (props.pins) where given, else the library's. */
export const pinLabels = (s: Sym): string[] => {
  const own = s.props.pins?.split(/[/,\s]+/).filter(Boolean) ?? [];
  return LIB[s.type].pins.map((p, i) => own[i] || p[2]);
};
export const wireNode = (id: string) => `w|${id}`;

export interface Junction {
  x: number;
  y: number;
  node: string;
}
export interface Potential {
  name: string;
  ptype: PType;
}
export interface PotConflict {
  it: Placed;
  a: string;
  b: string;
}

export interface CableCrossing {
  wireId: string;
  /** crossing point on the page */
  pt: Pt;
  /** assigned core designation, or '' when the cable has too few cores */
  core: string;
}
export interface CableInfo {
  it: Placed;
  cores: string[];
  cross: CableCrossing[];
}

/** IEC 81346 structure identifiers of a device: = function, ++ installation site, + location, # user-defined. */
export interface StructId {
  fn: string;
  site: string;
  loc: string;
  ud: string;
}
export const STRUCT_KEYS = ['fn', 'site', 'loc', 'ud'] as const;
export const STRUCT_PREFIX: Record<keyof StructId, string> = { fn: '=', site: '++', loc: '+', ud: '#' };

/** "=A1++B2+PANO1#X" for the identifiers that are set. */
export const structText = (v: Partial<StructId>) => STRUCT_KEYS.map((k) => (v[k] ? STRUCT_PREFIX[k] + v[k] : '')).join('');

/** Device key "=fn(++site)+loc(#ud)-tag"; without ++ and # it is the classic "=fn+loc-tag". */
export const deviceKey = (v: StructId, tag: string) => `=${v.fn}${v.site ? '++' + v.site : ''}+${v.loc}${v.ud ? '#' + v.ud : ''}-${tag}`;

/** Splits a device key back into its identifiers and tag. */
export function parseDeviceKey(key: string): (StructId & { tag: string }) | null {
  const m = /^=([^+#]*?)(?:\+\+([^+#]*?))?\+([^#]*?)(?:#(.*))?-([^-]*)$/.exec(key);
  return m ? { fn: m[1], site: m[2] ?? '', loc: m[3], ud: m[4] ?? '', tag: m[5] } : null;
}

const pickStruct = (...layers: (Partial<Record<keyof StructId, string | undefined>> | undefined)[]): StructId =>
  Object.fromEntries(STRUCT_KEYS.map((k) => [k, layers.map((l) => l?.[k]).find((v) => !!v) ?? ''])) as unknown as StructId;
/** The identifiers of `v` that differ from `ref`. */
const diffStruct = (v: StructId, ref: StructId): Partial<StructId> => Object.fromEntries(STRUCT_KEYS.filter((k) => v[k] !== ref[k]).map((k) => [k, v[k]]));

export const DEFAULT_SETTINGS: ProjectSettings = { wireNo: 'seq', wireNoPot: false };
export const cableCores = (s: Sym) =>
  (s.props.cores ?? '1,2,3,PE').split(',').map((c) => c.trim()).filter(Boolean);

export interface Analysis {
  /** the analysed project */
  pr: Project;
  uf: UnionFind;
  pu: UnionFind;
  junctions: Junction[][];
  all: Placed[];
  brk: Map<string, Placed[]>;
  /** device key (=fn+loc-tag) → main functions */
  mains: Map<string, Placed[]>;
  /** device key → auxiliary functions */
  auxs: Map<string, Placed[]>;
  /** symbol id → device key "=fn+loc-tag" */
  devKey: Map<string, string>;
  /** symbol id → tag as shown on its page (structure only where it differs from the page) */
  devLabel: Map<string, string>;
  /** device key → name relative to the project defaults (for lists and messages) */
  devName: Map<string, string>;
  auxLabels: Map<string, string[]>;
  pot: Map<string, Potential>;
  conflicts: PotConflict[];
  /** net root → order of first appearance (1-based) */
  netNo: Map<string, number>;
  /** net root → displayed wire number (manual, potential name or automatic) */
  netLabel: Map<string, string>;
  /** cable symbol id → crossings */
  cables: Map<string, CableInfo>;
  /** wire id → cable core running over it */
  wireCore: Map<string, { cable: Sym; core: string }>;
  /** net root → member count */
  size: Map<string, number>;
  /** valve solenoid device key → fluid valves that name it (props.sol / sol2) */
  solValves: Map<string, Placed[]>;
  /** fluid symbol id → device keys of its solenoids, in order */
  valveSols: Map<string, string[]>;
}

const push = <K, V>(m: Map<K, V[]>, k: K, v: V) => {
  const l = m.get(k);
  if (l) l.push(v);
  else m.set(k, [v]);
};

export const placedOrder = (a: Placed, b: Placed) => a.pi - b.pi || a.s.x - b.s.x || a.s.y - b.s.y;

/**
 * Connection definition points: each gives the wire it lies on its cross-section, colour and wire number, ahead of
 * the wire's own properties (written into the wire, so every list, the 3D wiring and the exports see them).
 */
export function applyConnectionDefinitions(pr: Project) {
  for (const page of pr.pages)
    for (const s of page.syms) {
      if (s.type !== 'cdp') continue;
      const w = page.wires.find((x) => x.pts.slice(1).some((q, k) => onSeg(s.x, s.y, x.pts[k], q)));
      if (!w) continue;
      if (s.props.cs) w.cs = s.props.cs;
      if (s.props.col) w.col = s.props.col;
      if (s.props.wno) w.no = s.props.wno;
    }
}

/** `parts`: the parts lookup; with it, auxiliary contacts are numbered from their part's function template. */
export function analyze(pr: Project, parts?: PartLookup): Analysis {
  applyConnectionDefinitions(pr);
  // pc: potentials for short-circuit detection. Like pu, except that of contactors whose
  // outputs land on the same wires (a reversing pair swapping two phases) only the first
  // passes potentials: the pair is interlocked, so that is not a short circuit.
  const uf = new UnionFind(), pu = new UnionFind(), pc = new UnionFind(), poles: Placed[] = [];
  const junctions: Junction[][] = [], all: Placed[] = [];
  const cables = new Map<string, CableInfo>(), wireCore = new Map<string, { cable: Sym; core: string }>();
  const brk = new Map<string, Placed[]>(), mains = new Map<string, Placed[]>(), auxs = new Map<string, Placed[]>();
  const devKey = new Map<string, string>(), devLabel = new Map<string, string>(), devName = new Map<string, string>();
  const solValves = new Map<string, Placed[]>(), valveSols = new Map<string, string[]>(), fluidPins: string[] = [];
  const pv = pickStruct(pr);
  const link = (a: string, b: string) => {
    uf.union(a, b);
    pu.union(a, b);
    pc.union(a, b);
  };

  pr.pages.forEach((page, pi) => {
    const gv = pickStruct(page, pv), single = page.kind === 'single';
    const boxes = page.syms.filter((s) => LIB[s.type]?.role === 'box');
    /** innermost structure box around a point */
    const boxAt = (x: number, y: number) => {
      let best: Sym | undefined, area = Infinity;
      for (const b of boxes) {
        const w = boxW(b), h = boxH(b);
        if (x >= b.x && x <= b.x + w && y >= b.y && y <= b.y + h && w * h < area) {
          best = b;
          area = w * h;
        }
      }
      return best;
    };
    type Pnt = { x: number; y: number; n: string[]; w: number };
    const pts = new Map<string, Pnt>();
    const at = (x: number, y: number) => {
      const k = kxy(x, y);
      let o = pts.get(k);
      if (!o) pts.set(k, (o = { x, y, n: [], w: 0 }));
      return o;
    };
    for (const s of page.syms) {
      const L = LIB[s.type];
      if (!L) continue;
      const it = { s, pi };
      all.push(it);
      pinsW(s).forEach((p, i) => {
        const n = pinNode(s, i);
        uf.find(n);
        pu.find(n);
        if (!single) at(p[0], p[1]).n.push(n);
      });
      (L.pass || []).forEach(([a, b]) => {
        pu.union(pinNode(s, a), pinNode(s, b));
        if (!isMainPoles(s.type)) pc.union(pinNode(s, a), pinNode(s, b));
      });
      if (isMainPoles(s.type) && L.pass) poles.push(it);
      if (L.role === 'brk' && s.props.name) push(brk, s.props.name, it);
      if (L.fluid) {
        L.pins.forEach((_, i) => fluidPins.push(pinNode(s, i)));
        const sols = [s.props.sol, s.props.sol2].map((t) => (t || '').trim()).filter(Boolean);
        if (sols.length) {
          const own = pickStruct(s, boxAt(s.x, s.y), gv);
          const keys = sols.map((t) => deviceKey(own, t));
          valveSols.set(s.id, keys);
          for (const k of keys) push(solValves, k, it);
        }
      }
      if (s.tag && L.role !== 'box') {
        const ctx = pickStruct(boxAt(s.x, s.y), gv), own = pickStruct(s, ctx), key = deviceKey(own, s.tag);
        devKey.set(s.id, key);
        devLabel.set(s.id, structText(diffStruct(own, ctx)) + '-' + s.tag);
        if (!devName.has(key)) devName.set(key, structText(diffStruct(own, pv)) + '-' + s.tag);
        if (L.role === 'main') push(mains, key, it);
        if (L.role === 'aux') push(auxs, key, it);
      }
    }
    for (const w of single ? [] : page.wires) {
      const n = wireNode(w.id);
      uf.find(n);
      pu.find(n);
      w.pts.forEach((p, i) => {
        const o = at(p[0], p[1]);
        o.n.push(n);
        o.w += i === 0 || i === w.pts.length - 1 ? 1 : 2;
      });
    }
    const plist = [...pts.values()];
    for (const o of plist) for (let i = 1; i < o.n.length; i++) link(o.n[0], o.n[i]);
    for (const w of page.wires)
      for (let i = 0; i < w.pts.length - 1; i++) {
        const a = w.pts[i], b = w.pts[i + 1];
        for (const o of plist)
          if (onSeg(o.x, o.y, a, b)) {
            link(wireNode(w.id), o.n[0]);
            o.w += 2;
          }
      }
    junctions[pi] = plist.filter((o) => o.w >= 3).map((o) => ({ x: o.x, y: o.y, node: o.n[0] }));

    // cable definition lines: every wire segment crossing the line becomes a core, in order
    for (const s of page.syms) {
      if (LIB[s.type]?.role !== 'cable') continue;
      const [a, b] = cableSegment(s), horiz = a[1] === b[1];
      const cross: { wireId: string; pt: Pt; t: number }[] = [];
      for (const w of page.wires)
        for (let i = 0; i < w.pts.length - 1; i++) {
          const p = w.pts[i], q = w.pts[i + 1];
          if (horiz && p[0] === q[0]) {
            const x = p[0], y = a[1];
            if (x > Math.min(a[0], b[0]) && x < Math.max(a[0], b[0]) && y > Math.min(p[1], q[1]) && y < Math.max(p[1], q[1]))
              cross.push({ wireId: w.id, pt: [x, y], t: Math.abs(x - a[0]) });
          } else if (!horiz && p[1] === q[1]) {
            const y = p[1], x = a[0];
            if (y > Math.min(a[1], b[1]) && y < Math.max(a[1], b[1]) && x > Math.min(p[0], q[0]) && x < Math.max(p[0], q[0]))
              cross.push({ wireId: w.id, pt: [x, y], t: Math.abs(y - a[1]) });
          }
        }
      cross.sort((m, n) => m.t - n.t);
      const cores = cableCores(s);
      const info: CableInfo = { it: { s, pi }, cores, cross: cross.map((c, i) => ({ wireId: c.wireId, pt: c.pt, core: cores[i] ?? '' })) };
      cables.set(s.id, info);
      for (const c of info.cross) if (c.core) wireCore.set(c.wireId, { cable: s, core: c.core });
    }
  });

  for (const g of brk.values()) for (let i = 1; i < g.length; i++) link(pinNode(g[0].s, 0), pinNode(g[i].s, 0));
  // terminal bridges: terminals of one strip sharing a bridge id are connected
  const bridges = new Map<string, Placed[]>();
  for (const it of all) if (LIB[it.s.type].role === 'term' && it.s.props.bridge) push(bridges, `${devKey.get(it.s.id)}|${it.s.props.bridge}`, it);
  for (const g of bridges.values()) for (let i = 1; i < g.length; i++) link(pinNode(g[0].s, 0), pinNode(g[i].s, 0));

  // potentials: named by potential points, typed by interruption points as fallback
  const outs = new Map<string, Set<string>>();
  for (const it of poles) {
    const L = LIB[it.s.type], key = L.pass!.map(([, b]) => uf.find(pinNode(it.s, b))).sort().join('|');
    const tags = outs.get(key) ?? outs.set(key, new Set()).get(key)!;
    if (tags.size && !tags.has(it.s.tag)) continue; // second contactor of a reversing pair
    tags.add(it.s.tag);
    L.pass!.forEach(([a, b]) => pc.union(pinNode(it.s, a), pinNode(it.s, b)));
  }
  const pot = new Map<string, Potential>(), conflicts: PotConflict[] = [], seen = new Map<string, string>();
  for (const it of all)
    if (LIB[it.s.type].role === 'pot') {
      const r = pu.find(pinNode(it.s, 0)), c = pot.get(r), nm = it.s.props.name || '';
      const rc = pc.find(pinNode(it.s, 0)), other = seen.get(rc);
      if (other && nm && other !== nm) conflicts.push({ it, a: other, b: nm });
      else if (nm && !other) seen.set(rc, nm);
      if (!c || !c.name) pot.set(r, { name: nm, ptype: it.s.props.ptype || '' });
    }
  for (const it of all)
    if (it.s.type === 'brk' && it.s.props.ptype) {
      const r = pu.find(pinNode(it.s, 0));
      if (!pot.has(r)) pot.set(r, { name: '', ptype: it.s.props.ptype });
    }

  // auxiliary contact numbering (EPLAN/IEC: 13-14 NO, 21-22 NC; overload 95-96/97-98)
  for (const l of mains.values()) l.sort(placedOrder);
  const auxLabels = new Map<string, string[]>();
  for (const [tag, list] of auxs) {
    list.sort(placedOrder);
    const m = (mains.get(tag) || [])[0];
    let n = m && ['pb_no', 'pb_nc', 'estop'].includes(m.s.type) ? 1 : 0;
    // the part's function template: its free contacts of each kind, taken in the order written
    const free: TermContact[] = m?.s.part && parts ? parseTerms(parts(m.s.part)?.terms) : [];
    const take = (kind: TermContact['kind']) => {
      const i = free.findIndex((c) => c.kind === kind);
      return i < 0 ? undefined : free.splice(i, 1)[0].pins;
    };
    for (const { s } of list) {
      const L = LIB[s.type];
      if (isMainPoles(s.type)) {
        auxLabels.set(s.id, L.pins.map((p) => p[2]));
        continue;
      }
      if (!L.kind && s.type !== 'co') {
        auxLabels.set(s.id, L.pins.map((p) => p[2]));
        continue;
      }
      let lab: string[];
      const own = free.length && !(s.props.pins ?? '').trim() ? take(s.type === 'co' ? 'co' : L.kind === 'nc' ? 'nc' : 'no') : undefined;
      if (own) lab = own;
      else if (s.type === 'co') {
        n++;
        lab = [n + '1', n + '2', n + '4'];
      } else if (m && m.s.type === 'ol3') lab = L.kind === 'nc' ? ['95', '96'] : ['97', '98'];
      else if (s.type === 'no_t' || s.type === 'nc_t' || s.type === 'no_toff' || s.type === 'nc_toff') lab = L.kind === 'nc' ? ['15', '16'] : ['17', '18'];
      else {
        n++;
        lab = L.kind === 'nc' ? [n + '1', n + '2'] : [n + '3', n + '4'];
      }
      const ov = (s.props.pins || '').split(/[/,\-\s]+/).filter(Boolean);
      auxLabels.set(s.id, ov.length === lab.length ? ov : lab);
    }
  }

  const netNo = new Map<string, number>(), size = new Map<string, number>();
  for (const key of [...uf.keys()]) {
    const r = uf.find(key);
    size.set(r, (size.get(r) || 0) + 1);
  }
  // wire numbering: manual/frozen number > potential name (optional) > automatic scheme
  const settings = { ...DEFAULT_SETTINGS, ...pr.settings };
  const manual = new Map<string, string>(), potName = new Map<string, string>();
  pr.pages.forEach((page) =>
    page.wires.forEach((w) => {
      const r = uf.find(wireNode(w.id));
      if (w.no && !manual.has(r)) manual.set(r, w.no);
    }),
  );
  for (const it of all) if (LIB[it.s.type].role === 'pot' && it.s.props.name) potName.set(uf.find(pinNode(it.s, 0)), it.s.props.name);
  const netLabel = new Map<string, string>(), counters = new Map<string, number>();
  const next = (key: string) => {
    const n = (counters.get(key) || 0) + 1;
    counters.set(key, n);
    return n;
  };
  // pipes (nets touching a fluid port) get no automatic wire number
  const pipes = new Set(fluidPins.map((n) => uf.find(n)));
  let k = 0;
  pr.pages.forEach((page, pi) =>
    page.wires.forEach((w) => {
      const r = uf.find(wireNode(w.id));
      if (netNo.has(r)) return;
      netNo.set(r, ++k);
      if (pipes.has(r) && !manual.has(r)) return;
      let label = manual.get(r) || (settings.wireNoPot ? potName.get(r) : undefined);
      if (!label) {
        if (settings.wireNo === 'page') label = `${pi + 1}${String(next('p' + pi)).padStart(2, '0')}`;
        else if (settings.wireNo === 'pagecol') {
          const col = colOf(Math.min(...w.pts.map((p) => p[0])));
          label = `${pi + 1}${String(col).padStart(2, '0')}.${next(`c${pi}.${col}`)}`;
        } else label = String(next('seq'));
      }
      netLabel.set(r, label);
    }),
  );

  return { pr, uf, pu, junctions, all, brk, mains, auxs, devKey, devLabel, devName, auxLabels, pot, conflicts, netNo, netLabel, size, cables, wireCore, solValves, valveSols };
}

export const potentialOf = (A: Analysis, node: string) => A.pot.get(A.pu.find(node));

export const isConnected = (A: Analysis, s: Sym, i: number) => (A.size.get(A.uf.find(pinNode(s, i))) || 1) > 1;

/** Human-readable connection point, e.g. "-K1:A1", "-X1:3". */
export function pinName(A: Analysis, s: Sym, i: number): string {
  const L = LIB[s.type];
  if (L.role === 'pot') return s.props.name || '?';
  const dev = deviceName(A, s);
  if (L.role === 'term') return `${dev}:${s.props.pinNo || '?'}`;
  if (L.role === 'plc') return `${dev}:${s.props.addr || '?'}`;
  const lab = L.role === 'aux' ? (A.auxLabels.get(s.id) || [])[i] : pinLabels(s)[i];
  return dev + (lab ? ':' + lab : '');
}

/** Device name relative to the project's = and + (e.g. "-K1" or "=P2+MCC2-K1"). */
export function deviceName(A: Analysis, s: Sym): string {
  const k = A.devKey.get(s.id);
  return k ? A.devName.get(k) ?? k : '-?';
}

export const keyName = (A: Analysis, key: string) => A.devName.get(key) ?? key;

export const netLabelOf = (A: Analysis, node: string) => A.netLabel.get(A.uf.find(node)) ?? '';
